import { useContext, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular, SendFilled } from '@fluentui/react-icons';
import { SignInContext } from '@modules/users';
import { useCreateSubmission } from '../../../api';
import { TCreateSubmissionDialogState, TCreateSubmissionDto } from '../../../types/submissions.types';
import { useNavigate } from 'react-router-dom';
import { ButtonLoading } from '@shared/components';
import SubmissionFormContent, { TFormDataSubmission } from '../../SubmissionFormContent';

const useStyles = makeStyles({
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

/**
 * CreateSubmissionDialog
 * ---------------------------------
 * Dialog component for creating a new submission.
 * Uses a single state object for dialog control.
 */
const CreateSubmissionDialog = ({
  state,
  setState,
}: {
  state: TCreateSubmissionDialogState;
  setState: (state: TCreateSubmissionDialogState) => void;
}) => {
  const navigate = useNavigate();
  const { user } = useContext(SignInContext);
  const classes = useStyles();

  // Pre-select the current user as the submission lead.
  const initialValues = { leads: [user._id] };

  const createSubmissionMutation = useCreateSubmission();

  const onSubmit = (data: TFormDataSubmission) => {
    const submissionData: TCreateSubmissionDto = {
      title: data.title,
      product: state.productId,
      jurisdiction: data.jurisdiction[0],
      dueDate: data.dueDate || undefined, // Optional due date.
      leads: data.leads,
    };

    createSubmissionMutation.mutate(submissionData, {
      onSuccess: (submissionId) => {
        // Close dialog on success.
        setState({ ...state, isOpen: false });
        // UX improvement: navigate after a short delay.
        setTimeout(() => {
          navigate(`/product/${state.productId}/submissions/${submissionId}`);
        }, 1000);
      },
    });
  };

  // Create a ref to the form component.
  const formRef = useRef<{ submit: () => void }>(null);

  return (
    <Dialog
      open={state.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) setState({ ...state, isOpen: false });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            <div className="flex items-center space-x-2">
              <SendFilled />
              <span>Prepare submission for {state.productName}</span>
            </div>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {/* The shared form component handles form rendering and validation. */}
            <SubmissionFormContent
              ref={formRef}
              initialValues={initialValues}
              jurisdictions={state.jurisdictions}
              onSubmit={onSubmit}
            />
          </DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              loading={createSubmissionMutation.isPending}
              onClick={() => formRef.current?.submit()}
            >
              Create
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateSubmissionDialog;
